import React, { useState, FormEvent, useEffect } from 'react';
// import NaijaStates from "naija-state-local-government";
import LetsgoIcon from '/asset/letsgo.svg';
import Content from '../components/Content';
import Selector from '../components/Selector';
import locationIcon from '/asset/location.svg';
import storeIcon from '/asset/store.svg';

import { useDispatch, useSelector } from 'react-redux';
import {
  fetchStoreBanner,
  fetchStoreInfo,
  fetchStores,
} from '../store/productSlice';
import { addStoreId } from '../store/setupSlice';
import { AppDispatch } from '../store/store';
import { RootState } from '../store/store';
import Alerts from '../components/Alerts';
import { useSearchParams } from 'react-router-dom';
import productService from '../services/productService';
import { Store } from '../interfaces';
import { toast } from 'react-toastify';

type WithLocationSelectionProps = {
  companyID?: number | string;
};

type StoreCollection = Store[] | null | undefined;

const withLocationSelectionForm = <P extends WithLocationSelectionProps>(
  WrappedComponent: React.ComponentType<P>
) => {
  const WithFormPopup: React.FC<P> = (props) => {
    const [searchParams] = useSearchParams();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const sessionId = searchParams.get('sessionId');
    const [useFranchise, setUseFranchise] = useState<Record<string, boolean>>(
      {}
    );
    const [mimicWhatsAppPhoneNumber, setMimicWhatsAppPhoneNumber] =
      useState<string>('');
    const { companyID } = props;
    const [continueToStore, setContinueToStore] = useState(false);
    // const states = useSelector((state: RootState) => state.products.states);
    const { storeId } = useSelector((state: RootState) => state.setup);
    const dispatch = useDispatch<AppDispatch>();
    const [percentageComplete, setPercentageComplete] = useState<number>(0);
    const { stores, storeInfo, storeLoading } = useSelector(
      (state: RootState) => state.products
    );

    const [uniqueRegions, setUniqueRegions] = useState<string[]>([]);
    const [franchisesByRegion, setFranchisesByRegion] = useState<
      Record<string, string[]>
    >({});
    const [selectedRegion, setSelectedRegion] = useState<string>('');
    const [selectedFranchise, setSelectedFranchise] = useState<string>('');
    const [selectedStoreId, setSelectedStoreId] = useState<number>(0);

    //Function to get all regions in stores
    const getAllUniqueRegions = (stores: StoreCollection): string[] => {
      // Using Set automatically ensures uniqueness
      if (stores) {
        const uniqueRegions = new Set<string>();

        stores.forEach((store) => {
          if (store.region) {
            uniqueRegions.add(store.region);
          } else {
            uniqueRegions.add('Other');
          }
        });
        // Convert back to array and sort alphabetically
        return Array.from(uniqueRegions).sort();
      } else {
        return [];
      }
    };

    //Get unique franchises in a region
    const getUniqueFranchisesInRegion = (
      stores: StoreCollection,
      region: string
    ): string[] => {
      if (stores) {
        const storesInRegion = stores.filter((store: Store) => {
          if (!store.region && region === 'Other') {
            return true;
          }
          return store.region === region;
        });

        const franchiseNames = storesInRegion.map((store: Store) => {
          const franchiseName = store.name.split(' ')[0]
            ? store.name.split(' ')[0]
            : 'Others';
          return franchiseName;
        });

        const uniqueFranchises = franchiseNames.filter(
          (value: string, index: number, self: string[]) =>
            self.indexOf(value) === index
        );

        const hasDuplicateFranchise =
          uniqueFranchises.length < franchiseNames.length;

        if (hasDuplicateFranchise) {
          setUseFranchise((prev) => ({
            ...prev,
            [region]: true,
          }));
        }

        return uniqueFranchises;
      } else {
        return [];
      }
    };

    //Get stores by region and first word in name
    const getStoresByRegionAndFranchise = ({
      stores,
      region,
      franchise,
    }: {
      stores: StoreCollection;
      region: string;
      franchise: string;
    }): Store[] => {
      if (!stores) return [];

      if (region === 'Other') {
        const hasFranchise = franchise && franchise !== 'Others';
        return stores.filter((store: Store) => {
          if (hasFranchise) {
            return (
              store.name.split(' ')[0].toLowerCase() === franchise.toLowerCase()
            );
          } else {
            return !store.region;
          }
        });
      }
      return region && franchise
        ? stores.filter(
            (store: Store) =>
              store.region === region &&
              store.name.split(' ')[0].toLowerCase() === franchise.toLowerCase()
          )
        : [];
    };

    //Get stores by region only
    const getStoresByRegion = ({
      stores,
      region,
    }: {
      stores: StoreCollection;
      region: string;
    }): Store[] =>
      !stores || !region
        ? []
        : stores.filter((store: Store) =>
            region === 'Other' ? !store.region : store.region === region
          );

    useEffect(() => {
      const all_regions = getAllUniqueRegions(stores);
      setUniqueRegions(all_regions);
    }, [stores]);

    useEffect(() => {
      if (uniqueRegions) {
        if (uniqueRegions.length > 0) {
          const f: Record<string, string[]> = {};
          uniqueRegions.forEach((region) => {
            const fr = getUniqueFranchisesInRegion(stores, region);
            f[region] = fr;
          });
          setFranchisesByRegion(f);
        }
        if (uniqueRegions && uniqueRegions.length === 1) {
          setSelectedRegion(uniqueRegions[0]);
        }
      }
    }, [uniqueRegions, stores]);

    useEffect(() => {
      if (stores && selectedStoreId) {
        const thisStore = stores.find(
          (store) => Number(store.id) === selectedStoreId
        );
        const whatsAppNumber = thisStore?.mimicWhatsAppPhoneNumber;
        setMimicWhatsAppPhoneNumber(whatsAppNumber || '');
      }
    }, [selectedStoreId, stores]);

    useEffect(() => {
      if (storeId && storeId.toString() !== localStorage.getItem('storeId')) {
        localStorage.setItem('storeId', storeId.toString());
      }
      if (companyID && storeId) {
        dispatch(fetchStoreInfo(storeId));
      }
    }, [dispatch, companyID, storeId]);

    useEffect(() => {
      if (storeInfo) {
        setContinueToStore(true);
      }
    }, [storeInfo]);

    useEffect(() => {
      if (companyID && !continueToStore) {
        dispatch(fetchStores(companyID ? companyID?.toString() : ''))
          .unwrap()
          .then(() => {
            dispatch(fetchStoreBanner(companyID ? companyID?.toString() : ''));
          });
      }
    }, [dispatch, companyID, continueToStore]);

    const displayFranchise =
      !!selectedRegion &&
      !!franchisesByRegion &&
      Array.isArray(franchisesByRegion[selectedRegion]) &&
      franchisesByRegion[selectedRegion].length > 1 &&
      !!useFranchise[selectedRegion];

    useEffect(() => {
      let completed = 0;

      if (!selectedRegion) {
        completed = 0;
      } else if (displayFranchise && !selectedFranchise) {
        completed = 33;
      } else if (!selectedStoreId) {
        completed = 66;
      } else {
        completed = 100;
      }

      setPercentageComplete(completed);
    }, [selectedRegion, selectedFranchise, selectedStoreId, displayFranchise]);
    useEffect(() => {
      if (stores && stores.length === 1) {
        dispatch(addStoreId(stores[0].id));
      }
    }, [dispatch, stores, storeId]);

    useEffect(() => {
      if (
        franchisesByRegion &&
        franchisesByRegion[selectedRegion] &&
        franchisesByRegion[selectedRegion].length === 1
      ) {
        setSelectedFranchise(franchisesByRegion[selectedRegion][0]);
      }
    }, [selectedRegion, franchisesByRegion]);

    const handleSubmit = async (e: FormEvent) => {
      e.preventDefault();
      if (!selectedStoreId) {
        alert('Please fill in all fields.');
        return;
      }
      if (selectedStoreId.toString() !== storeId?.toString()) {
        dispatch(addStoreId(Number(selectedStoreId)));
      }
      if (sessionId) {
        const result = await productService.selectMimicStore({
          storeId: selectedStoreId,
          sessionId,
        });
        if (result) {
          if (result.success) {
            setErrorMessage(
              'Store selected successfully, redirecting to WhatsApp...'
            );
            const normalizedNumber = mimicWhatsAppPhoneNumber.replace(
              /\D/g,
              ''
            );
            const url = `https://wa.me/${normalizedNumber}?text=`;
            window.open(url, '_blank');
          } else {
            setErrorMessage(result.message);
            toast.error(result.message);
          }
          setContinueToStore(true);
        }
      } else {
        setContinueToStore(true);
      }
    };

    if (!continueToStore && stores && !storeId) {
      // Single store: skip the selection form; auto-selection effect will handle it
      if (stores.length === 1) {
        return <Alerts message="Loading..." />;
      }
      if (stores.length > 1) {
        return (
          <div className="bg-white !w-[100vw] !h-[100vh]">
            <div
              className="bg-secondary-500 
                !w-full !h-[200px] text-white
                flex flex-col items-left justify-center
                "
            >
              <Content>
                <div className="text-[24px] font-[700] text-white">
                  {sessionId ? 'Select Store' : 'Start Here'}
                </div>
                <div
                  className="text-[16px] 
                text-white font-[300]"
                >
                  {sessionId
                    ? 'Select store to continue on WhatsApp'
                    : 'Select your region, choose a franchise, and pick the store closest to you.'}
                </div>
              </Content>
            </div>
            <Content>
              <div>
                <div className="w-full h-full">
                  <div
                    className="bg-white rounded-2xl p-6 w-full
                    bg-shadow bg-white"
                  >
                    <form onSubmit={handleSubmit} className="space-y-4">
                      {uniqueRegions && uniqueRegions.length > 1 && (
                        <Selector
                          count={1}
                          title="Region"
                          subtitle="Select your region from the list below"
                          selectedValue={selectedRegion}
                          onChangeHandler={(e) => {
                            const this_state = e.target.value;
                            setSelectedRegion(this_state);
                            setSelectedFranchise('');
                            setSelectedStoreId(0);
                            setMimicWhatsAppPhoneNumber('');
                          }}
                          nullValue=""
                          nullText="-- Select Store Region --"
                          icon={locationIcon}
                          options={uniqueRegions.map((state) => ({
                            value: state,
                            name: state,
                          }))}
                        />
                      )}
                      {selectedRegion !== '' &&
                        franchisesByRegion &&
                        franchisesByRegion[selectedRegion] &&
                        useFranchise[selectedRegion] &&
                        franchisesByRegion[selectedRegion].length > 1 && (
                          <Selector
                            count={2}
                            title="Franchise"
                            subtitle="Select franchise from the list below"
                            selectedValue={selectedFranchise}
                            onChangeHandler={(e) => {
                              const this_franchise = e.target.value;
                              setSelectedFranchise(this_franchise);
                            }}
                            nullValue=""
                            nullText="-- Select Franchise --"
                            icon={storeIcon}
                            options={franchisesByRegion[selectedRegion].map(
                              (franchise) => ({
                                value: franchise,
                                name: franchise,
                              })
                            )}
                          />
                        )}
                      {selectedRegion !== '' && (
                        <Selector
                          count={
                            uniqueRegions.length === 1
                              ? 1
                              : uniqueRegions.length > 1 && displayFranchise
                                ? 3
                                : 2
                          }
                          title="Select Store"
                          subtitle="Choose your store from the dropdown below"
                          selectedValue={selectedStoreId}
                          onChangeHandler={(e) => {
                            const storeId = e.target.value;
                            setSelectedStoreId(Number(storeId));
                          }}
                          nullValue={0}
                          nullText="Select a store"
                          icon={locationIcon}
                          options={(useFranchise[selectedRegion] &&
                          selectedFranchise
                            ? getStoresByRegionAndFranchise({
                                stores,
                                region: selectedRegion,
                                franchise: selectedFranchise,
                              })
                            : getStoresByRegion({
                                stores,
                                region: selectedRegion,
                              })
                          ).map((store) => ({
                            value: store.id,
                            name: store.name ? store.name : `Store ${store.id}`,
                          }))}
                        />
                      )}
                      <button
                        type="submit"
                        disabled={percentageComplete !== 100}
                        className={` mt-[20px] h-[50px] 
                        text-white py-2 rounded-full m-auto
                        transition flex items-center 
                        justify-center font-bold text-[16px]
                        outline-none border-none
                        ${
                          percentageComplete === 100
                            ? 'bg-secondary-700 hover:bg-secondary-800 text-white cursor-pointer'
                            : '!bg-gray-200 cursor-not-allowed'
                        }
                        `}
                      >
                        <img
                          src={LetsgoIcon}
                          alt="Let's Go"
                          className="w-[20px]"
                        />
                        <span className="ml-2">
                          {sessionId ? 'Continue to WhatsApp' : "Let's Go"}
                        </span>
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </Content>
          </div>
        );
      }
    }
    if (storeLoading) {
      return <Alerts message="Loading stores..." />;
    }
    if (sessionId && continueToStore) {
      return <Alerts message={errorMessage} />;
    }
    if (!stores || stores.length === 0) {
      return <Alerts message="No store found." />;
    }
    if (storeId && continueToStore && !storeInfo) {
      // In-between state: we have a storeId and want to continue, but storeInfo hasn't loaded yet.
      return <Alerts message="Loading store information..." />;
    }
    if (storeId && storeInfo && continueToStore) {
      return <WrappedComponent {...(props as P)} />;
    }

    // Final fallback to ensure the component always returns something.
    return <Alerts message="Loading..." />;
  };
  return WithFormPopup;
};

export default withLocationSelectionForm;
